import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "@/state/hooks";
import {
  MoreVertical,
  ChevronLeft,
  Loader2,
  User,
  Phone,
  Paperclip,
  ArrowRightCircle,
  Edit,
  UserPlus,
  Reply,
  Image,
  Copy,
  Info,
  FolderOpen,
  Check,
  Trash2,
} from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import GiphyPopup from "@/ui/components/GiphyPopup";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { useStore } from "@/state/legacy_module_api";
import { ContentType, messageSchema, type Message } from "@/data/schemas";
import { ThreadViewMenuPopOver } from "./ThreadViewMenuPopOver";
import { generateUUID as uuidv4, formatTime, MessageType, MessageTypeHelper } from "@/core_bridge/module_api";
import { VList, VListHandle } from "virtua";
import ThreadSearchPopup from "./ThreadSearchPopup";
import MessageBubbleInfoPopup from "./MessageBubbleInfoPopup";
import ThreadMessagebar from "./ThreadMessagebar";
import { appConfig } from "@/app/module_api";
import { ErrorLogger } from "@/utils/module_api";
import { cn } from "@/utils/module_api";
import GroupMembersPopup from "./GroupMembersPopup";
import GroupMemberAddPopup from "./GroupMemberAddPopup";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/ui/dialog";

// Stable empty object reference to avoid re-renders with Zustand
const EMPTY_OBJ: Record<string, any> = {};
type CallLifecycleState =
  | "idle"
  | "starting"
  | "joining"
  | "active"
  | "ended"
  | "failed";

type ThreadViewProps = {
  receiverId?: string;
  senderId?: string;
  groupId?: string;
  messages?: Message[];
};

type NormalizedAttachment = {
  id?: string;
  url?: string;
  objectUrl?: string;
  encryptedUrl?: string;
  keyBase64?: string;
  mimeType?: string;
  filename?: string;
  size?: number;
  duration?: number;
};

const normalizeAttachment = (raw: any): NormalizedAttachment => {
  const encryptedUrl = raw?.encryptedUrl ?? raw?.encrypted_url ?? raw?.objectUrl ?? raw?.object_url ?? raw?.url;
  return {
    id: raw?.id ?? raw?.attachmentId ?? raw?.attachment_id,
    url: raw?.url,
    objectUrl: raw?.objectUrl ?? raw?.object_url,
    encryptedUrl,
    keyBase64: raw?.keyBase64 ?? raw?.key_base64 ?? raw?.encryptionKey ?? raw?.encryption_key,
    mimeType: raw?.mimeType ?? raw?.mime_type ?? raw?.contentType ?? raw?.content_type,
    filename: raw?.filename ?? raw?.fileName ?? raw?.file_name,
    size: typeof raw?.size === "number" ? raw.size : undefined,
    duration: typeof raw?.duration === "number" ? raw.duration : undefined,
  };
};

const sanitizeAttachmentPathPart = (value: string): string =>
  value.trim().replace(/[^A-Za-z0-9._-]/g, "_").replace(/^[._-]+|[._-]+$/g, "") || "attachment";

const AttachmentImagePreview = ({ attachment }: { attachment: NormalizedAttachment }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    const loadPreview = async () => {
      const encryptedObjectUrl = attachment.objectUrl || attachment.encryptedUrl || attachment.url;
      if (!encryptedObjectUrl) {
        setFailed(true);
        return;
      }

      try {
        setFailed(false);
        const { attachments } = await import("@/core_bridge/module_api");
        const { filesystem } = await import("@/services/module_api");

        const attachmentId = sanitizeAttachmentPathPart(attachment.id || encryptedObjectUrl);
        const filename = sanitizeAttachmentPathPart(attachment.filename || "image");
        const cachePath = `attachments/${attachmentId}/${filename}`;
        const cachedBlob = await filesystem.readFileToBlob?.({ path: cachePath });
        if (cancelled) return;

        if (cachedBlob) {
          createdUrl = URL.createObjectURL(cachedBlob);
          setPreviewUrl(createdUrl);
          return;
        }

        let baseUrl = encryptedObjectUrl;
        let keyBase64 = attachment.keyBase64;
        if (!keyBase64) {
          [baseUrl, keyBase64] = attachments.parseUrl(encryptedObjectUrl);
        }

        const response = await fetch(baseUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image attachment: ${response.statusText}`);
        }

        const encryptedData = new Uint8Array(await response.arrayBuffer());
        const decrypted = attachments.decrypt(encryptedData, keyBase64);
        const blob = new Blob([decrypted], { type: attachment.mimeType || "image/*" });
        await filesystem.writeFile({ path: cachePath, blob });
        if (cancelled) return;

        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      } catch (error) {
        console.warn("[attachment] image preview failed", error);
        if (!cancelled) setFailed(true);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [
    attachment.id,
    attachment.objectUrl,
    attachment.encryptedUrl,
    attachment.url,
    attachment.keyBase64,
    attachment.mimeType,
    attachment.filename,
  ]);

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={attachment.filename || "Image attachment"}
        className="max-h-80 max-w-64 rounded-lg object-contain"
      />
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 bg-black/10 rounded-lg">
      <Image className="w-5 h-5" />
      <div>
        <div className="text-sm font-medium">{attachment.filename || "Image"}</div>
        <div className="text-xs opacity-70">
          {failed
            ? "Tap to open image"
            : attachment.size
              ? `${Math.round(attachment.size / 1024)} KB`
              : "Loading image..."}
        </div>
      </div>
    </div>
  );
};

const ThreadView = (props: ThreadViewProps) => {
  const location = useLocation();
  const locationState = (location.state || {}) as any;
  const receiverId = props.receiverId ?? locationState.receiverId;
  const senderId = props.senderId ?? locationState.senderId;
  const groupId = props.groupId ?? locationState.groupId;

  const [giphyPopupOpened, setGiphyPopupOpened] = useState(false);
  const [searchPopupOpened, setSearchPopupOpened] = useState(false);
  const [messageInfoPopupOpened, setMessageInfoPopupOpened] = useState(false);
  const [membersPopupOpened, setMembersPopupOpened] = useState(false);
  const [addMemberPopupOpened, setAddMemberPopupOpened] = useState(false);
  const [menuPopoverOpen, setMenuPopoverOpen] = useState(false);
  const [messagePopoverOpen, setMessagePopoverOpen] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [callEntryOpen, setCallEntryOpen] = useState(false);
  const [callEntryMode, setCallEntryMode] = useState<"start" | "join">("start");
  const [pendingJoinMessage, setPendingJoinMessage] = useState<Message | null>(null);
  const [callLifecycleState, setCallLifecycleState] =
    useState<CallLifecycleState>("idle");
  const [callLifecycleMessage, setCallLifecycleMessage] = useState<string | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | undefined>();
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const receiverUser = useAppState().user.contactsById[receiverId || ""] || {} as any;
  const appState = useAppState() as any;
  const myUserId = appState?.myUserId;
  const tokenType = appState?.tokenType;
  const threadActions = useActions().thread || ({} as any);
  const {
    loadOlderMessages = async () => {},
    deleteMessage = async () => {},
    joinCall = async () => {},
    startCall = async () => {},
    sendMessage = async (_msg: any) => {},
  } = threadActions;
  const attachmentActions = useActions().attachment as any;
  const openAttachment = attachmentActions?.openAttachment || ((_opts: any) => {});
  const hilo = useAppState().hilo;
  const { contactsById } = useAppState().user;

  // Use stable empty object references to avoid infinite re-renders
  const selection = useStore((s: any) => s.selection ?? EMPTY_OBJ);
  const selectedThreadId = useStore((s: any) => s.selectedThreadId);
  const messagesByThread = useStore((s: any) => s.messagesByThread ?? EMPTY_OBJ);
  const hasMoreByThread = useStore((s: any) => s.hasMoreByThread ?? EMPTY_OBJ);
  const selectThread = useStore((s: any) => s.selectThread);

  const threadKey =
    selection.threadKey ||
    selection.groupId ||
    selection.withUserId ||
    selectedThreadId;
  const activeGroupId = groupId || selection.groupId || threadKey;
  const messages = props.messages || (threadKey ? messagesByThread[threadKey] || [] : []);
  const hasMoreOlderMessages = threadKey ? !!hasMoreByThread[threadKey] : false;
  const title = selection.title || "";
  const isLoading = false;
  const error = undefined;
  const isGroupThread = !!threadKey?.startsWith("group:");
  const threadMembers = (appState?.group?.threadMap?.[threadKey || ""]?.members || []) as Array<{
    userId: string;
  }>;
  const groupParticipantNames = useMemo(
    () =>
      threadMembers
        .map((member) => {
          const user = contactsById[member.userId];
          return user?.fullHandle || user?.username || member.userId;
        })
        .filter(Boolean),
    [contactsById, threadMembers]
  );

  const vlistHandleRef = useRef<VListHandle>(null);
  const shouldStickToBottom = useRef(true);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const userActions = useActions().user as any;
  const blockUserNew = userActions?.blockUserNew || (() => {});
  const unblockUserNew = userActions?.unblockUserNew || (() => {});
  const reportUserNew = userActions?.reportUserNew || (() => {});
  const communityActions = useActions().community as any;
  const handleCommunityGroupAcceptRequest = communityActions?.handleCommunityGroupAcceptRequest || (() => {});

  useEffect(() => {
    setIsTransitioning(false);
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Latest-ref so the viewport listener attaches once instead of re-attaching
  // on every message (the handler still sees the current messages.length).
  const scrollToBottomRef = useRef<() => void>(() => {});

  useEffect(() => {
    const handleViewportChange = () => {
      requestAnimationFrame(() => scrollToBottomRef.current());
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleViewportChange);
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (vlistHandleRef.current) {
      vlistHandleRef.current.scrollToIndex(messages.length - 1, {
        align: "end",
        smooth: true,
      });
    }
  };
  scrollToBottomRef.current = scrollToBottom;

  // Use switch-core formatTime utility
  const formatMessageDate = (timestamp: number) => formatTime(timestamp);

  // Layout: let the messages list fill available space and leave room for the input bar
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTransitioning) return;
    if (!isLoading) {
      scrollToBottom();
    }
  }, [messages.length, isLoading, isTransitioning]);

  const initialLoadCompleteRef = useRef(false);
  const isLoadingOlderRef = useRef(false);

  useEffect(() => {
    if (
      vlistHandleRef.current &&
      messages.length > 0 &&
      !initialLoadCompleteRef.current
    ) {
      requestAnimationFrame(() => {
        vlistHandleRef.current?.scrollTo(vlistHandleRef.current.scrollSize);
        initialLoadCompleteRef.current = true;
      });
    }
  }, [messages]);

  const handleScroll = (offset: number) => {
    if (!initialLoadCompleteRef.current) return;
    if (!vlistHandleRef.current) return;

    const TOP_THRESHOLD = 150;

    if (
      offset < TOP_THRESHOLD &&
      !isLoadingOlderRef.current &&
      hasMoreOlderMessages
    ) {
      isLoadingOlderRef.current = true;
      const prevScrollHeight = vlistHandleRef.current.scrollSize;
      loadOlderMessages().then(() => {
        requestAnimationFrame(() => {
          const newScrollHeight = vlistHandleRef.current?.scrollSize;
          if (vlistHandleRef.current && newScrollHeight) {
            vlistHandleRef.current.scrollTo(newScrollHeight - prevScrollHeight);
          }
          isLoadingOlderRef.current = false;
        });
      });
    } else {
      shouldStickToBottom.current =
        offset -
          vlistHandleRef.current.scrollSize +
          vlistHandleRef.current.viewportSize >=
        -1.5;
    }
  };

  const isOneToOne = receiverId && senderId;

  const handleMessageClick = (e: React.MouseEvent, message: Message) => {
    e.preventDefault();
    setSelectedMessage(message);
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setMessagePopoverOpen(true);
  };

  const resetCallEntryState = () => {
    setCallLifecycleState("idle");
    setCallLifecycleMessage(null);
  };

  const openCallEntryForStart = () => {
    setCallEntryMode("start");
    setPendingJoinMessage(null);
    resetCallEntryState();
    setCallEntryOpen(true);
  };

  const openCallEntryForJoin = (message: Message) => {
    setCallEntryMode("join");
    setPendingJoinMessage(message);
    resetCallEntryState();
    setCallEntryOpen(true);
  };

  const callStateLabel = (() => {
    switch (callLifecycleState) {
      case "starting":
        return "starting";
      case "joining":
        return "joining";
      case "active":
        return "active";
      case "ended":
        return "ended";
      case "failed":
        return "failed";
      default:
        return null;
    }
  })();

  const submitCallEntry = async () => {
    const callStateForProgress = callEntryMode === "start" ? "starting" : "joining";
    setCallLifecycleState(callStateForProgress);
    setCallLifecycleMessage(null);
    try {
      if (callEntryMode === "start") {
        await startCall();
        setCallLifecycleState("active");
        setCallEntryOpen(false);
        return;
      }

      if (!pendingJoinMessage) {
        throw new Error("Missing call room to join.");
      }
      await joinCall(pendingJoinMessage);
      setCallLifecycleState("active");
      setCallEntryOpen(false);
    } catch (err: any) {
      setCallLifecycleState("failed");
      setCallLifecycleMessage(err?.message || "Call action failed");
    }
  };

  return (
    <div data-name="thread-view" className="flex flex-col h-screen h-[100dvh] bg-background">
      {/* Header */}
      <div data-name="thread-header" className="flex items-center justify-between px-4 py-3 border-b h-14">
        <div className="flex items-center gap-3">
          <Button
            data-name="back-button"
            variant="ghost"
            size="icon"
            onClick={() => {
              selectThread?.({});
            }}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 data-name="thread-title" className="font-semibold">{title}</h1>
        </div>
        <ThreadViewMenuPopOver
          receiverId={receiverId}
          senderId={senderId}
          groupId={activeGroupId}
          onCallClick={openCallEntryForStart}
          onSearchClick={() => setSearchPopupOpened(true)}
          onShowMembers={() => setMembersPopupOpened(true)}
          onAddMember={() => setAddMemberPopupOpened(true)}
          open={menuPopoverOpen}
          onOpenChange={setMenuPopoverOpen}
          trigger={
            <Button variant="ghost" size="icon">
              <MoreVertical className="w-5 h-5" />
            </Button>
          }
        />
      </div>

      {/* Error display */}
      {error && (
        <div data-name="error-display" className="p-4 bg-destructive/10 border-b text-center">
          <p className="text-destructive">Error loading thread: {error}</p>
        </div>
      )}

      {/* Messages area */}
      <div data-name="messages-area" className="flex-1 overflow-hidden min-h-0 flex flex-col" ref={messagesContainerRef}>
        {isLoading ? (
          <div data-name="loading-state" className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading messages...</p>
          </div>
        ) : (
          <VList
            style={{
              flex: 1,
              height: "100%",
              minHeight: 0,
              paddingBottom: "96px", // reserve space for input bar
            }}
            reverse
            ref={vlistHandleRef}
            onScroll={handleScroll}
            className="px-4"
          >
            {messages.length === 0 ? (
              <div data-name="empty-state" className="text-center py-12">
                <p className="text-muted-foreground">
                  No messages yet. Be the first to say hello!
                </p>
              </div>
            ) : (
              messages
                .filter((m: Message) => {
                  if (!hilo?.isConfigured) return true;
                  const currentEnvironment = hilo.environment;
                  if (currentEnvironment !== "hi" && currentEnvironment !== "lo") {
                    return true;
                  }

                  const messageEnvironment = m.environment?.trim().toLowerCase();
                  if (!messageEnvironment || messageEnvironment === "standard") {
                    return true;
                  }

                  return messageEnvironment === currentEnvironment;
                })
                .map((message: Message, idx: number) => {
                  const myId = myUserId || senderId;
                  const threadPartnerId =
                    (receiverId && receiverId !== myId ? receiverId : undefined) ||
                    selection.withUserId ||
                    selection.receiverId ||
                    selection.groupId ||
                    undefined;
                  const isGif = message.contentType === ContentType.GIPHY;
                  // Check if this is an attachment - either has attachment field or has attachment-like contentType
                  const isAttachmentType = ["IMAGE", "VIDEO", "AUDIO", "FILE", "ATTACHMENT"].includes(message.contentType || "");
                  // Try to get attachment data from attachment field or parse from content
                  let attachmentData = message.attachment ? normalizeAttachment(message.attachment) : undefined;
                  if (!attachmentData && isAttachmentType && message.content) {
                    try {
                      const parsed = JSON.parse(message.content);
                      const normalized = normalizeAttachment(parsed);
                      if (normalized.encryptedUrl || normalized.mimeType) {
                        attachmentData = normalized;
                      }
                    } catch {
                      // Not JSON, ignore
                    }
                  }
                  const isAttachment = !!attachmentData;
                  const isDelivered = message.status === "DELIVERED";
                  const isDeleted = !!message.deletedAt;
                  const isReply = !!message.replyToMessageId;
                  const displayContent =
                    typeof message.content === "string" && message.content.trim().length
                      ? message.content
                      : message.encryptedContent
                        ? "[Encrypted]"
                        : "[No content]";
                  const friendlySystemContent =
                    MessageTypeHelper.isSystemMessage(message.type) && message.type !== MessageType.MESSAGE
                      ? (typeof message.content === "string" && message.content.trim().length
                        ? message.content
                        : MessageTypeHelper.getFriendlyContent(message.type))
                      : null;
                  const resolvedSenderId = message.senderId || threadPartnerId;
                  const nameOfSender =
                    contactsById[resolvedSenderId || ""]?.username ||
                    contactsById[threadPartnerId || ""]?.username ||
                    threadPartnerId ||
                    "Unknown User";
                  let finalIsSent = false;
                  if (myId) {
                    if (message.senderId) {
                      finalIsSent = message.senderId === myId;
                    } else if (message.receiverId && threadPartnerId) {
                      // If senderId is missing, assume it's sent by me if the receiver matches the other participant
                      finalIsSent = message.receiverId === threadPartnerId;
                    } else if (!message.senderId && !message.receiverId) {
                      // Fallback for locally created messages without ids yet
                      finalIsSent = true;
                    }
                  }

                  return (
                    <div
                      key={`${message.id}-${idx}`}
                      data-name="message-bubble-group"
                      data-message-id={message.id}
                      data-sent={finalIsSent}
                      className={cn(
                        "mb-3 flex w-fit max-w-[80%] flex-col gap-2",
                        finalIsSent ? "ml-auto items-end" : "mr-auto items-start"
                      )}
                    >
                      {/* Reply preview */}
                      {isReply && (() => {
                        const replyToMessageId = message.replyToMessageId?.trim().toLowerCase();
                        const originalMessage = messages.find(
                          (m) => m.id?.trim().toLowerCase() === replyToMessageId
                        );
                        const originalSenderId = originalMessage?.senderId;
                        const previewAuthor = originalMessage
                          ? originalSenderId === myId
                            ? "Me"
                            : contactsById[originalSenderId || ""]?.username ||
                              contactsById[threadPartnerId || ""]?.username ||
                              originalSenderId ||
                              "Unknown User"
                          : "Original message";
                        const previewText = (() => {
                          if (!originalMessage) return "Message unavailable";
                          if (originalMessage.type === MessageType.CALL) return "Call";
                          if (originalMessage.contentType === ContentType.GIPHY) return "GIF";
                          const previewContentType = originalMessage.contentType || "";
                          if (["IMAGE", "VIDEO", "AUDIO", "FILE", "ATTACHMENT"].includes(previewContentType)) {
                            return previewContentType === "AUDIO" ? "Voice message" : "Attachment";
                          }
                          if (typeof originalMessage.content === "string" && originalMessage.content.trim().length) {
                            return originalMessage.content;
                          }
                          return "Message";
                        })();

                        return (
                          <div
                            className={cn(
                              "w-full rounded-xl px-3 py-2",
                              finalIsSent
                                ? "bg-primary/30"
                                : "bg-muted"
                            )}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              <div
                                className={cn(
                                  "w-1 self-stretch rounded-full",
                                  finalIsSent ? "bg-primary-foreground/80" : "bg-primary"
                                )}
                              />
                              <div className="min-w-0">
                                <div
                                  className={cn(
                                    "text-xs font-medium",
                                    finalIsSent ? "text-primary-foreground" : "text-primary"
                                  )}
                                >
                                  {previewAuthor}
                                </div>
                                <div
                                  className={cn(
                                    "text-xs truncate max-w-full",
                                    finalIsSent ? "text-primary-foreground/90" : "text-foreground/70"
                                  )}
                                >
                                  {previewText}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      <div
                        data-name="message-bubble"
                        className={cn(
                          "w-full rounded-2xl px-4 py-2 cursor-pointer",
                          finalIsSent
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        )}
                        onClick={(e) => handleMessageClick(e, message)}
                      >
                        <p className="text-xs opacity-70 mb-1 flex items-center gap-1">
                          <span>{finalIsSent ? "Me" : nameOfSender}</span>
                          <span>-</span>
                          <span>{formatMessageDate(message.createdAt)}</span>
                          {finalIsSent && isDelivered && <span aria-label="Sent">✓</span>}
                        </p>

                      {/* Call message */}
                      {message.type === MessageType.CALL && (
                        <button
                          className={cn(
                            "flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer",
                            finalIsSent
                              ? "bg-primary-foreground/20 hover:bg-primary-foreground/30"
                              : "bg-primary/10 hover:bg-primary/20"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            openCallEntryForJoin(message);
                          }}
                        >
                          <Phone className={cn(
                            "w-5 h-5",
                            finalIsSent ? "text-primary-foreground" : "text-primary"
                          )} />
                          <div className="text-left">
                            <div className="font-medium">Join Call</div>
                            <div className="text-xs opacity-70">
                              Open join options
                            </div>
                          </div>
                        </button>
                      )}

                      {/* Call joined */}
                      {message.type === MessageType.CALL_JOINED && (
                        <div>
                          <span>📞 Joined call in room </span>
                          <strong>{message.roomId}</strong>
                        </div>
                      )}

                      {/* GIF message */}
                      {isGif && (
                        <img
                          src={message.content}
                          alt="GIF"
                          className="max-w-60 rounded-lg"
                        />
                      )}

                      {/* Regular text message */}
                      {message.type === MessageType.MESSAGE &&
                        !isGif &&
                        !isAttachment && (
                          <div>{displayContent}</div>
                        )}

                      {/* Community updated */}
                      {message.type === MessageType.COMMUNITY_UPDATED && (
                        <small className="flex items-center gap-1">
                          <Edit className="w-4 h-4" />
                          {contactsById[message.senderId]?.username || "Unknown user"}{" "}
                          added you to community.
                        </small>
                      )}

                      {/* Join request */}
                      {message.type === MessageType.COMMUNITY_THREAD_JOIN_REQUEST && (
                        <div className="flex items-center gap-2">
                          <UserPlus className="w-5 h-5" />
                          <span>
                            {contactsById[message.senderId]?.username || "Unknown user"}{" "}
                            wants to join this community group.
                          </span>
                        </div>
                      )}

                      {message.type === MessageType.COMMUNITY_THREAD_UPDATED && (
                        <small>✅ Updated a community.</small>
                      )}

                      {message.type === MessageType.THREAD_UPDATED && (
                        <small>✅ Group settings updated.</small>
                      )}

                      {message.type === MessageType.THREAD_CREATED && (
                        <small>✅ Created this group.</small>
                      )}

                      {friendlySystemContent &&
                        message.type !== MessageType.CALL &&
                        message.type !== MessageType.CALL_JOINED &&
                        message.type !== MessageType.COMMUNITY_UPDATED &&
                        message.type !== MessageType.COMMUNITY_THREAD_JOIN_REQUEST &&
                        message.type !== MessageType.COMMUNITY_THREAD_UPDATED &&
                        message.type !== MessageType.THREAD_UPDATED &&
                        message.type !== MessageType.THREAD_CREATED &&
                        message.type !== MessageType.EXIT_THREAD && (
                          <small>{friendlySystemContent}</small>
                        )}

                      {message.type === MessageType.EXIT_THREAD && (
                        <div className="flex items-center gap-2">
                          <ArrowRightCircle className="w-5 h-5" />
                          <span>
                            {contactsById[message.senderId]?.username || "Unknown user"}{" "}
                            has left the group.
                          </span>
                        </div>
                      )}

                      {/* Encrypted message that can't be decrypted */}
                      {!isGif &&
                        typeof message.content !== "string" &&
                        message.type === MessageType.MESSAGE &&
                        message.encryptedContent && (
                          <small>🚫 This message cannot be decrypted.</small>
                        )}

                      {/* Attachment */}
                      {isAttachment && attachmentData && (
                        <div
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = attachmentData.objectUrl || attachmentData.encryptedUrl || (attachmentData as any).url;
                            const keyBase64 = attachmentData.keyBase64 || (attachmentData as any).key_base64;
                            if (url) {
                              openAttachment?.({
                                id: attachmentData.id,
                                url,
                                objectUrl: attachmentData.objectUrl,
                                keyBase64,
                                mimeType: attachmentData.mimeType,
                                filename: attachmentData.filename,
                              });
                            }
                          }}
                        >
                          {attachmentData.mimeType?.startsWith("image/") ? (
                            <AttachmentImagePreview attachment={attachmentData} />
                          ) : attachmentData.mimeType?.startsWith("video/") ? (
                            <div className="flex items-center gap-2 p-2 bg-black/10 rounded-lg">
                              <FolderOpen className="w-5 h-5" />
                              <div>
                                <div className="text-sm font-medium">{attachmentData.filename || "Video"}</div>
                                <div className="text-xs opacity-70">
                                  {attachmentData.duration ? `${attachmentData.duration}s` : "Video attachment"}
                                </div>
                              </div>
                            </div>
                          ) : attachmentData.mimeType?.startsWith("audio/") ? (
                            <div className="flex items-center gap-2 p-2 bg-black/10 rounded-lg">
                              <Paperclip className="w-5 h-5" />
                              <div>
                                <div className="text-sm font-medium">{attachmentData.filename || "Audio"}</div>
                                <div className="text-xs opacity-70">
                                  {attachmentData.duration ? `${attachmentData.duration}s` : "Audio message"}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 p-2 bg-black/10 rounded-lg">
                              <Paperclip className="w-5 h-5" />
                              <div>
                                <div className="text-sm font-medium">{attachmentData.filename || "File"}</div>
                                <div className="text-xs opacity-70">
                                  {attachmentData.mimeType || "Attachment"}
                                  {attachmentData.size ? ` • ${Math.round(attachmentData.size / 1024)} KB` : ""}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {isDeleted && <small>🚫 You deleted this message.</small>}
                    </div>
                  </div>
                  );
                })
            )}
          </VList>
        )}
      </div>

      {/* Message actions menu - positioned at click location */}
      {messagePopoverOpen && menuPosition && selectedMessage && (
        <>
          {/* Backdrop to close menu */}
          <div
            data-name="menu-backdrop"
            className="fixed inset-0 z-40"
            onClick={() => setMessagePopoverOpen(false)}
          />
          {/* Menu */}
          <div
            data-name="message-context-menu"
            className="fixed z-50 bg-popover border rounded-md shadow-lg py-1 min-w-[160px]"
            style={{
              left: Math.min(menuPosition.x, window.innerWidth - 180),
              top: Math.min(menuPosition.y, window.innerHeight - 300),
            }}
          >
            {selectedMessage.type === "CALL" && (
              <button
                data-name="menu-join-call"
                className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
                onClick={() => {
                  openCallEntryForJoin(selectedMessage);
                  setMessagePopoverOpen(false);
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Join Call
              </button>
            )}
            <button
              data-name="menu-reply"
              className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
              onClick={() => {
                setReplyToMessage(selectedMessage);
                setMessagePopoverOpen(false);
              }}
            >
              <Reply className="w-4 h-4 mr-2" />
              Reply
            </button>
            <button
              data-name="menu-copy"
              className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
              onClick={() => {
                if (selectedMessage.content) {
                  navigator.clipboard.writeText(selectedMessage.content);
                  toast("Message copied");
                }
                setMessagePopoverOpen(false);
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </button>
            <button
              data-name="menu-info"
              className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
              onClick={() => {
                setMessageInfoPopupOpened(true);
                setMessagePopoverOpen(false);
              }}
            >
              <Info className="w-4 h-4 mr-2" />
              Info
            </button>
            {selectedMessage.attachment && (
              <button
                data-name="menu-open-file"
                className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
                onClick={() => {
                  openAttachment(selectedMessage);
                  setMessagePopoverOpen(false);
                }}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Open File
              </button>
            )}
            {(selectedMessage.type as any) === "COMMUNITY_THREAD_JOIN_REQUEST" && (
              <button
                data-name="menu-accept-request"
                className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent"
                onClick={async () => {
                  await handleCommunityGroupAcceptRequest(selectedMessage);
                  setMessagePopoverOpen(false);
                }}
              >
                <Check className="w-4 h-4 mr-2" />
                Accept Request
              </button>
            )}
            <button
              data-name="menu-delete"
              className="flex items-center w-full px-3 py-2 text-sm hover:bg-accent text-destructive"
              onClick={async () => {
                try {
                  await deleteMessage(selectedMessage);
                } catch (error) {
                  ErrorLogger.logError(error as Error, "ThreadView.tsx");
                  toast.error("Error deleting message");
                }
                setMessagePopoverOpen(false);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </>
      )}

      {/* Message bar - sticky at bottom for Safari compatibility */}
      {receiverUser?.isBlocked ? (
        <div data-name="message-bar-blocked" className="sticky bottom-0 border-t bg-muted z-10 mt-auto p-4 flex items-center justify-center gap-4">
          <span className="text-muted-foreground">User is blocked</span>
          <button
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
            onClick={() => unblockUserNew({ user: receiverUser })}
          >
            Unblock
          </button>
        </div>
      ) : (
        <div data-name="message-bar-container" className="sticky bottom-0 border-t bg-background z-10 mt-auto">
          <ThreadMessagebar
            replyToMessage={replyToMessage}
            onClearReply={() => setReplyToMessage(null)}
          />
        </div>
      )}

      {/* Search popup */}
      <ThreadSearchPopup
        opened={searchPopupOpened}
        onPopupClose={() => setSearchPopupOpened(false)}
        receiverId={receiverId}
        senderId={senderId}
        groupId={activeGroupId}
      />

      {/* Members popup */}
      <GroupMembersPopup
        open={membersPopupOpened}
        onClose={() => setMembersPopupOpened(false)}
        groupId={activeGroupId}
      />

      {/* Add member popup */}
      <GroupMemberAddPopup
        open={addMemberPopupOpened}
        onClose={() => setAddMemberPopupOpened(false)}
        groupId={activeGroupId}
      />

      {/* Message info popup */}
      <MessageBubbleInfoPopup
        opened={messageInfoPopupOpened}
        onPopupClose={() => setMessageInfoPopupOpened(false)}
        message={selectedMessage}
      />

      <Dialog
        open={callEntryOpen}
        onOpenChange={(isOpen) => {
          setCallEntryOpen(isOpen);
          if (!isOpen) {
            setPendingJoinMessage(null);
            resetCallEntryState();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {callEntryMode === "start" ? "Start Call" : "Join Call"}
            </DialogTitle>
            <DialogDescription>
              {isGroupThread ? "Group call" : "Direct call"} in {title || "this thread"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
              <div className="font-medium">Transport</div>
              <div className="text-xs text-muted-foreground">Jitsi</div>
            </div>

            {isGroupThread && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Participants</div>
                {groupParticipantNames.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    No group participants resolved yet.
                  </div>
                ) : (
                  <div className="max-h-28 overflow-auto rounded-md border p-2 text-xs">
                    {groupParticipantNames.map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {callEntryMode === "join" && pendingJoinMessage?.roomId && (
              <div className="text-xs text-muted-foreground">
                Room: {pendingJoinMessage.roomId}
              </div>
            )}

            {callStateLabel && (
              <div className="rounded-md border bg-muted/50 px-2 py-1 text-xs">
                State: {callStateLabel}
                {callLifecycleMessage ? ` - ${callLifecycleMessage}` : ""}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setCallEntryOpen(false);
                setPendingJoinMessage(null);
                resetCallEntryState();
              }}
            >
              Close
            </Button>
            <Button onClick={submitCallEntry}>
              {callEntryMode === "start" ? "Start Call" : "Join Call"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Giphy popup */}
      <GiphyPopup
        opened={giphyPopupOpened}
        onPopupClose={() => setGiphyPopupOpened(false)}
        onGifSelect={(src) => {
          try {
            const message = messageSchema.parse({
              id: uuidv4(),
              appId: appConfig.appId,
              type: MessageType.MESSAGE,
              status: "SENT",
              content: src,
              senderId: senderId,
              receiverId: receiverId || undefined,
              groupId: senderId && receiverId ? undefined : groupId || undefined,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              contentType: "GIPHY",
            });
            sendMessage(message);
            toast.success("GIF sent!");
          } catch (error) {
            ErrorLogger.logError(error as Error, "ThreadView.tsx");
            console.error("Failed to send GIF:", error);
            toast.error("Failed to send GIF");
          }
        }}
        title="Send a GIF"
      />

      <div ref={bottomRef} />
    </div>
  );
};

export default ThreadView;
